/**
 * Longevity Samples Manager - Automatic Sample Addition/Removal for Longevity Products
 *
 * This script automatically adds or removes sample products from the Shopify cart
 * based on the presence and quantity of Longevity products (1-Month, 3-Months, 6-Months).
 *
 * Features:
 * - Auto-adds samples when Longevity products are added
 * - Auto-removes samples when Longevity products are removed
 * - Syncs sample quantities with parent Longevity product quantities
 * - Prevents manual addition of sample products
 * - Works alongside existing GWP (Gift With Purchase) system
 *
 * @version 1.0.0
 * @author Sunny Within Development Team
 */

class LongevitySamplesManager {
  /**
   * Initializes the Longevity Samples Manager.
   */
  constructor() {
    this.isProcessing = false;
    this.checkTimer = null; // Timer for delayed checks
    this.isUpdatingUI = false; // Flag to prevent reacting to own UI updates
    this.config = window.longevitySamples; // Cache config from liquid template
    this.miniCart = null; // Will be set in init()

    // Bind methods to preserve context
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.checkSampleRules = this.checkSampleRules.bind(this);

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Core initialization logic. Sets up event listeners and starts monitoring.
   */
  init() {
    // Early validation - if no config is available, exit
    if (!this.config || !this.config.enabled) {
      return;
    }

    // Cache mini-cart reference for better performance
    this.miniCart = document.querySelector('mini-cart');

    // Listen for standard and custom cart update events
    const cartUpdateEvents = ['cart:updated', 'cartUpdated', 'ajaxProduct:added'];
    cartUpdateEvents.forEach((event) => {
      document.addEventListener(event, this.handleCartUpdate);
    });

    // Subscribe to Shopify's modern pub/sub events
    if (!window.Shopify?.designMode && window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }

    // Perform initial check after a short delay to let other systems initialize
    setTimeout(() => {
      this.checkSampleRules();
    }, 500);
  }

  /**
   * Handles incoming cart update events with proper sequencing.
   * @param {Event} event - The cart update event object.
   */
  handleCartUpdate(event) {
    // Ignore updates triggered by this script to prevent infinite loops
    if (this.isProcessing || this.isUpdatingUI || event?.detail?.source === 'longevity-samples') {
      return;
    }

    // Clear any existing timer to debounce rapid events
    clearTimeout(this.checkTimer);

    // Special handling for Skio events
    if (event.type === 'cartUpdated') {
      this.checkTimer = setTimeout(() => {
        this.checkSampleRules();
      }, 300); // Longer delay for Skio events
      return;
    }

    // Set timer to check sample rules after other operations settle
    this.checkTimer = setTimeout(() => {
      this.checkSampleRules();
    }, 200);
  }

  /**
   * Main logic loop. Fetches the cart and manages sample products.
   */
  async checkSampleRules() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const cart = await this.getCurrentCart();

      if (!cart || !cart.items) {
        return;
      }

      // Step 1: Find all Longevity products in cart
      const longevityItems = this.findLongevityProducts(cart);

      // Step 2: Calculate required samples based on rules
      const requiredSamples = this.calculateRequiredSamples(longevityItems);

      // Step 3: Find existing samples in cart
      const existingSamples = this.findSamplesInCart(cart);

      // Step 4: Sync samples (add, remove, update quantities)
      await this.syncSamples(cart, requiredSamples, existingSamples);
    } catch (error) {
      this.showNotification('Error managing samples. Please refresh the page.', 'error');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Fetches the complete current cart state from Shopify's Cart API.
   * @returns {Promise<object>} A promise that resolves with the cart object.
   */
  async getCurrentCart() {
    const cacheBuster = Date.now();
    const response = await fetch(`/cart.js?t=${cacheBuster}`);

    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }

    return await response.json();
  }

  /**
   * Finds all Longevity products in the cart that have subscriptions.
   * Only products with selling plans (subscriptions) qualify for samples.
   * @param {object} cart - The current cart object.
   * @returns {Array} Array of Longevity product items with subscriptions.
   */
  findLongevityProducts(cart) {
    const triggerProductIds = this.config.rules.map((rule) => rule.triggerProductId);

    return cart.items.filter((item) => {
      const itemProductId = item.product_id;
      const isLongevityProduct = triggerProductIds.includes(itemProductId);

      // Check if item has a subscription (selling plan) selected
      const hasSubscription = item.selling_plan_allocation !== null && item.selling_plan_allocation !== undefined;

      // Only return Longevity products that have subscriptions
      return isLongevityProduct && hasSubscription;
    });
  }

  /**
   * Calculates required samples based on Longevity products and rules.
   * @param {Array} longevityItems - Array of Longevity product items.
   * @returns {Array} Array of required sample objects.
   */
  calculateRequiredSamples(longevityItems) {
    const requiredSamples = [];

    longevityItems.forEach((longevityItem) => {
      const itemProductId = longevityItem.product_id;
      const rule = this.config.rules.find((r) => r.triggerProductId === itemProductId);

      if (!rule) {
        return;
      }

      rule.samples.forEach((sampleConfig) => {
        const requiredQty = longevityItem.quantity * sampleConfig.quantityMultiplier;

        requiredSamples.push({
          variantId: sampleConfig.variantId,
          quantity: requiredQty,
          parentProductId: itemProductId,
          parentProductTitle: longevityItem.product_title,
          parentVariantId: longevityItem.variant_id,
          parentKey: longevityItem.key,
        });
      });
    });

    return requiredSamples;
  }

  /**
   * Finds existing sample products in the cart.
   * @param {object} cart - The current cart object.
   * @returns {Array} Array of existing sample items.
   */
  findSamplesInCart(cart) {
    return cart.items.filter((item) => {
      return item.properties?._longevitySample === 'true';
    });
  }

  /**
   * Synchronizes cart samples with required samples.
   * @param {object} cart - The current cart object.
   * @param {Array} requiredSamples - Array of required samples.
   * @param {Array} existingSamples - Array of existing samples.
   */
  async syncSamples(cart, requiredSamples, existingSamples) {
    // Group required samples by variant ID for easier comparison
    const requiredByVariantId = {};
    requiredSamples.forEach((sample) => {
      if (!requiredByVariantId[sample.variantId]) {
        requiredByVariantId[sample.variantId] = {
          ...sample,
          totalQuantity: 0,
        };
      }
      requiredByVariantId[sample.variantId].totalQuantity += sample.quantity;
    });

    // Group existing samples by variant ID
    const existingByVariantId = {};
    existingSamples.forEach((sample) => {
      const sampleVariantId = sample.variant_id;
      if (!existingByVariantId[sampleVariantId]) {
        existingByVariantId[sampleVariantId] = [];
      }
      existingByVariantId[sampleVariantId].push(sample);
    });

    // Track updates needed
    const updates = {};

    // Check each required sample
    for (const [variantId, required] of Object.entries(requiredByVariantId)) {
      const existing = existingByVariantId[variantId]?.[0]; // Get first instance

      if (!existing) {
        // Sample doesn't exist - add it
        await this.addSample(required);
      } else if (existing.quantity !== required.totalQuantity) {
        // Sample exists but wrong quantity - update it
        updates[existing.key] = required.totalQuantity;
      }
    }

    // Check for samples that should be removed
    for (const [variantId, samples] of Object.entries(existingByVariantId)) {
      if (!requiredByVariantId[variantId]) {
        // Sample exists but shouldn't - remove it
        samples.forEach((sample) => {
          updates[sample.key] = 0;
        });
      }
    }

    // Execute batch update if needed
    if (Object.keys(updates).length > 0) {
      await this.updateCartWithChanges(updates);
    }
  }

  /**
   * Adds a sample product to the cart.
   * @param {object} sample - The sample configuration object.
   */
  async addSample(sample) {
    if (!sample?.variantId) {
      return;
    }

    try {
      // Define line item properties to track the sample
      const properties = {
        _longevitySample: 'true',
        _freeSample: 'true',
        _parentProductTitle: sample.parentProductTitle || '',
      };

      const sections = this.getSectionsToRender();
      const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

      const response = await fetch(cartAddUrl, {
        ...(window.fetchConfig?.('json') || {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
        body: JSON.stringify({
          id: sample.variantId,
          quantity: sample.quantity,
          properties: properties,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || 'Failed to add sample');
      }

      this.updateCartUI(result);

      // Find the added item to get its title for notification
      const addedItem = result.items?.find((item) => item.variant_id === sample.variantId);
      const itemTitle = addedItem?.product_title || 'Sample';

      this.showNotification(`🎁 Added: ${itemTitle}`, 'success');
    } catch (error) {
      this.showNotification(`Failed to add sample (variant ${sample.variantId})`, 'error');
    }
  }

  /**
   * Updates multiple cart items in a single API call.
   * @param {object} updates - Object mapping cart keys to new quantities.
   */
  async updateCartWithChanges(updates) {
    try {
      const sections = this.getSectionsToRender();
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';

      const response = await fetch(cartUpdateUrl, {
        ...(window.fetchConfig?.('json') || {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
        body: JSON.stringify({
          updates,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || 'Failed to update cart');
      }

      this.updateCartUI(result);
    } catch (error) {
      this.showNotification('Failed to update samples', 'error');
    }
  }

  /**
   * Gets sections to render for cart updates.
   * @returns {Array} Array of section IDs.
   */
  getSectionsToRender() {
    if (this.miniCart?.getSectionsToRender) {
      return this.miniCart.getSectionsToRender().map((s) => s.id);
    }

    // Fallback sections if miniCart is not available
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  /**
   * Updates cart UI using cached mini-cart reference.
   * @param {Object} result - Cart API response.
   */
  updateCartUI(result) {
    // Set flag to ignore events triggered by UI updates
    this.isUpdatingUI = true;

    try {
      // Update cart UI using mini-cart's render method
      if (this.miniCart?.renderContents) {
        this.miniCart.renderContents(result);
      }

      // Also dispatch a custom event for other systems
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('longevity:samples-updated', {
            detail: {
              cart: result,
              source: 'longevity-samples',
            },
          })
        );
      }, 100);
    } finally {
      // Clear flag after update completes
      setTimeout(() => {
        this.isUpdatingUI = false;
      }, 400);
    }
  }

  /**
   * Displays a temporary notification to the user.
   * @param {string} message - The notification message.
   * @param {string} type - The notification type (success, error, info).
   */
  showNotification(message, type = 'info') {
    const colors = {
      success: '#28a745',
      error: '#dc3545',
      info: '#17a2b8',
    };

    const notification = document.createElement('div');
    notification.className = 'longevity-notification';
    notification.style.cssText = `
      position: fixed; 
      top: 20px; 
      right: 20px; 
      z-index: 10001;
      background: ${colors[type]}; 
      color: white; 
      padding: 12px 20px;
      border-radius: 5px; 
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font: 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      max-width: 320px; 
      opacity: 0; 
      transform: translateX(110%);
      transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    });

    // Auto-remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(110%)';
      setTimeout(() => notification.remove(), 400);
    }, 3000);
  }

  /**
   * Cleanup method to clear timers.
   */
  destroy() {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
  }
}

// Instantiate the manager when script loads
if (window.longevitySamples?.enabled) {
  window.longevitySamplesManager = new LongevitySamplesManager();
}
